
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { getDateAvailability } from './utils/dateAvailability';
import { TeacherAvailabilityException, WeeklySchedule } from '@/types/teacherAvailability';
import { ExceptionFormDialog } from './ExceptionFormDialog';
import { useTeacherAvailabilityExceptions } from '@/hooks/useTeacherAvailabilityExceptions';

interface DateDetailsProps {
  date: Date;
  weeklySchedule: WeeklySchedule;
  exceptions: TeacherAvailabilityException[];
  teacherCode?: string;
  onExceptionUpdate?: () => void;
}

export function DateDetails({ date, weeklySchedule, exceptions, teacherCode, onExceptionUpdate }: DateDetailsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { deleteException } = useTeacherAvailabilityExceptions(teacherCode);
  const availability = getDateAvailability(date, weeklySchedule, exceptions);
  const dateStr = format(date, 'yyyy-MM-dd');
  const existingException = exceptions.find(ex => ex.exception_date === dateStr);

  const handleDeleteException = async () => {
    if (existingException && confirm('คุณต้องการลบวันหยุดการสอนนี้หรือไม่?')) {
      const success = await deleteException(existingException.id);
      if (success && onExceptionUpdate) {
        onExceptionUpdate();
      }
    }
  };

  const handleExceptionAdded = () => {
    setIsDialogOpen(false);
    if (onExceptionUpdate) {
      onExceptionUpdate();
    }
  };

  if (availability.status === 'unavailable') {
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground">ไม่มีช่วงเวลาทำงานในวันนี้</p>
        {teacherCode && (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setIsDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              เพิ่มวันหยุดการสอน
            </Button>
          </div>
        )}
        <ExceptionFormDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          teacherCode={teacherCode || ''}
          selectedDate={dateStr}
          onSuccess={handleExceptionAdded}
        />
      </div>
    );
  }

  if (availability.status === 'exception') {
    return (
      <div className="space-y-3">
        <Badge variant="secondary" className="bg-orange-100 text-orange-800 mb-2">
          วันหยุดการสอน (ชั่วคราว)
        </Badge>
        
        {availability.type === 'NO_WORK' ? (
          <p>ไม่รับสอนทั้งวัน</p>
        ) : (
          <div>
            <p className="font-medium mb-2">ช่วงเวลาที่ไม่รับสอน:</p>
            <p className="text-red-600 mb-3">{availability.timeSlots.join(', ')}</p>
            
            {availability.basicTimeSlots && availability.basicTimeSlots.length > 0 && (
              <div>
                <p className="font-medium mb-2">ช่วงเวลารับสอน (พื้นฐาน):</p>
                <div className="space-y-1">
                  {availability.basicTimeSlots.map((slot, index) => (
                    <div key={index} className="text-sm text-green-600">
                      • {slot.start_time} - {slot.end_time}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {availability.reason && (
          <p className="text-sm text-muted-foreground mt-2">
            เหตุผล: {availability.reason}
          </p>
        )}

        {teacherCode && (
          <div className="flex gap-2 pt-2">
            <Button 
              size="sm" 
              variant="destructive"
              onClick={handleDeleteException}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              ลบวันหยุดการสอน
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Badge variant="secondary" className="bg-green-100 text-green-800 mb-2">
        วันที่รับสอน
      </Badge>
      <div className="space-y-1">
        {availability.timeSlots.map((slot, index) => (
          <div key={index} className="text-sm">
            • {slot}
          </div>
        ))}
      </div>

      {teacherCode && (
        <div className="flex gap-2 pt-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setIsDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            เพิ่มวันหยุดการสอน
          </Button>
        </div>
      )}

      <ExceptionFormDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        teacherCode={teacherCode || ''}
        selectedDate={dateStr}
        onSuccess={handleExceptionAdded}
      />
    </div>
  );
}
